import React from 'react'
import _ from 'underscore'
import { tz } from 'moment-timezone'
import styled from 'styled-components'

import { Panel } from 'components/Panel'
import Spinner from 'components/Spinner'

import RotateDeviceMessage from 'pages/shared/RotateDeviceMessage'
import MaintenanceTrend from 'pages/shared/MaintenanceTrend'
import { parseAlerts } from 'pages/Device/ThermostatRthPanel/Chart/rthDataParser'

import BrowserCompatibility from 'helpers/browserCompatibility'

import useServerlessAlertHistory from 'hooks/useServerlessAlertHistory'
import useOrientation from 'hooks/useOrientation'
import useDeviceMaintenanceTrends from 'hooks/useDeviceMaintenanceTrends'

import { updateAlertsSeverity } from 'helpers/alerts'

import { Device } from 'types/Device'
import { DeviceMaintenanceTrend } from 'types/DeviceMetric'
import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'

type props = {
  device: Device
}

const MaintenanceTrendsPanel = ({ device }: props) => {
  const { deviceId, timeZone } = device
  const { maintenanceTrends, areMaintenanceTrendsLoaded } = useDeviceMaintenanceTrends(deviceId)
  const { LockIcon, tier } = usePremiumFeaturesContext()
  const isMobile = BrowserCompatibility.isMobile(navigator.userAgent)
  const { isPortrait } = useOrientation()
  const toTime = tz(device.timeZone).format('YYYY-MM-DD')
  const fromTime = tz(device.timeZone).subtract(30, 'days').format('YYYY-MM-DD')
  const { alertsHistory, isAlertsHistoryLoaded } = useServerlessAlertHistory(deviceId, fromTime, toTime, timeZone)

  if (!areMaintenanceTrendsLoaded || !isAlertsHistoryLoaded) {
    return <Spinner variant="page" />
  }

  if (areMaintenanceTrendsLoaded && _.isEmpty(maintenanceTrends)) {
    return (<></>)
  }

  return (
    <S.Panel id="maintenance-trends" scrollToSection>
      <Panel.Header>
        <Panel.Title>Maintenance Trends
          <LockIcon featureCode="maintenance_trends" featureComponentType={'title'} />
        </Panel.Title>
      </Panel.Header>
      {isMobile && isPortrait
        ? (
          <RotateDeviceMessage />
        )
        : (
          <div>
            {areMaintenanceTrendsLoaded && maintenanceTrends && (
              _.map(maintenanceTrends, (maintenanceTrend: DeviceMaintenanceTrend) => {
                const sortedMetricsAndGroups = _.sortBy([...maintenanceTrend.metrics, ...maintenanceTrend.groups], ['sortPosition'])
                const alertCodes = _.sortBy([...maintenanceTrend.alertList], ['sortPosition'])
                const historyDetails = updateAlertsSeverity(alertsHistory?.historyDetails) ?? []
                const selectedCodes = _.pluck(alertCodes, 'code')
                const alerts = parseAlerts(historyDetails.filter((alert) => selectedCodes.includes(alert.code)), device.timeZone)
                return (
                  <MaintenanceTrend
                    key={maintenanceTrend.name}
                    trend={maintenanceTrend}
                    metricAndGroupList={sortedMetricsAndGroups}
                    alerts={alerts}
                    timeZone={timeZone}
                    deviceId={deviceId}
                    showMetricSelection
                    sendAnalyticsEvents
                    tier={tier}
                  />
                )
              })
            )}
          </div>
        )}
    </S.Panel>
  )
}

export default MaintenanceTrendsPanel

const S = {
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
  `
}
